import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { isMainSite, mainSiteUrl, isSingleRegion } from '@/utils/site';

const MAIN_SITE_URL = mainSiteUrl();

// ─── Route sets ───────────────────────────────────────────────────────
// MAIN site: only public-facing pages (marketing landing, login, signup,
// plus /sso for the rare case of cross-site link clicks back to main).
const MAIN_SITE_ROUTES = [
  {
    path: '/',
    name: 'landing',
    component: () => import('@/views/LandingView.vue'),
    meta: { public: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('@/views/SignupView.vue'),
    meta: { public: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
];

// REGION site: only the authenticated app.
// NO landing, NO login, NO signup — those live on the main site only.
// (Phase 1: /sso route removed — token is now passed via URL fragment.)
const REGION_SITE_ROUTES = [
  {
    path: '/',
    component: () => import('@/layouts/DashboardLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      // Default: redirect bare / to dashboard (region has no landing)
      { path: '', redirect: { name: 'dashboard' } },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { module: 'dashboard' },
      },
      {
        path: 'credentials',
        name: 'credentials',
        component: () => import('@/views/CredentialsView.vue'),
        meta: { module: 'credentials' },
      },
      {
        path: 'product-fields',
        name: 'product-fields',
        component: () => import('@/views/ProductFieldsView.vue'),
        meta: { module: 'product-schema' },
      },
      {
        path: 'products',
        redirect: { name: 'product-fields' },
      },
      {
        path: 'mappings',
        name: 'mappings',
        component: () => import('@/views/MappingsView.vue'),
        meta: { module: 'mappings' },
      },
      {
        path: 'mappings/:name',
        name: 'mapping-editor',
        component: () => import('@/views/MappingEditorView.vue'),
        meta: { module: 'mappings' },
      },
      {
        path: 'listings',
        name: 'listings',
        component: () => import('@/views/ListingsView.vue'),
        meta: { module: 'listings' },
      },
      {
        path: 'feeds',
        name: 'feeds',
        component: () => import('@/views/FeedsView.vue'),
        meta: { module: 'feeds' },
      },
      {
        path: 'orders',
        name: 'orders',
        component: () => import('@/views/OrdersView.vue'),
        meta: { module: 'orders' },
      },
      {
        path: 'api-access',
        name: 'api-access',
        component: () => import('@/views/ApiAccessView.vue'),
        meta: { module: 'api-access' },
      },
      // Schema Explorer switched off for now (CP-B10): its backend module was
      // deleted, so the page could only show errors. Restore together with
      // the commented-out api_schema_explorer_* endpoints in api/external.py.
      // {
      //   path: 'schema-explorer',
      //   name: 'schema-explorer',
      //   component: () => import('@/views/SchemaExplorer.vue'),
      //   meta: { module: 'product-schema' },
      // },
      {
        path: 'permissions',
        name: 'permissions',
        component: () => import('@/views/PermissionsView.vue'),
        meta: { adminOnly: true },
      },
      {
        // NOTE: not '/profile' — Frappe has a built-in www/profile page that
        // shadows it and returns "Not Permitted". Use '/account'.
        path: 'account',
        name: 'account',
        component: () => import('@/views/ProfileView.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
];

const routes = isMainSite() ? MAIN_SITE_ROUTES : REGION_SITE_ROUTES;

const router = createRouter({
  history: createWebHistory('/'),
  routes,
});

// Single-region deployments collapse main + region into one site. The default
// REGION_SITE_ROUTES set above doesn't include /login or /signup (those live
// on the main site in multi-region mode), so add them dynamically here when
// we're on a non-main site that needs public auth pages. Done via addRoute()
// because module-load-time isSingleRegion() is unreliable — main.js may not
// have finished its site_mode fetch yet — but addRoute() is safe to call any
// time and routes added BEFORE the first navigation are picked up correctly.
if (!isMainSite()) {
  // Insert the catch-all LAST so /login + /signup match first.
  router.addRoute({
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  });
  router.addRoute({
    path: '/signup',
    name: 'signup',
    component: () => import('@/views/SignupView.vue'),
    meta: { public: true },
  });
  // Re-add catch-all so the order is correct (login/signup before *).
  router.removeRoute('not-found');
  router.addRoute({
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  });
}

/**
 * If the URL has a `#mc_token=...` fragment (e.g. landed here from a main-site
 * signup or login that proxied to this region), drop it into the mc_session
 * cookie so subsequent API calls see the auth, and strip the fragment from
 * the address bar so a refresh doesn't re-process it.
 *
 * Runs synchronously the first time anything imports the router, BEFORE the
 * first beforeEach + before any whoami call — that's the whole point.
 */
function consumeTokenFragment() {
  const hash = window.location.hash || '';
  const m = hash.match(/[#&]mc_token=([^&]+)/);
  if (!m) return;
  const token = decodeURIComponent(m[1]);
  // Region domain. 12-hour TTL — same as the JWT itself. SameSite / Secure
  // set explicitly (CP-B16) rather than left to browser defaults; HttpOnly
  // cannot be set from JavaScript.
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `mc_session=${token}; path=/; max-age=${60 * 60 * 12}; SameSite=Lax${secure}`;
  // Scrub the fragment so refresh doesn't re-apply (and the token never shows
  // up in screenshots / shoulder-surf).
  history.replaceState(null, '', window.location.pathname + window.location.search);
}
consumeTokenFragment();

// ─── Guard ────────────────────────────────────────────────────────────
router.beforeEach(async (to) => {
  const auth = useAuthStore();

  if (!auth.checked) {
    await auth.checkSession();
  }

  // Region site: any auth-required nav with dead session bounces to login.
  // In single-region mode the login form lives on this very site (the SPA
  // router serves /login from REGION_SITE_ROUTES too), so don't cross-domain
  // redirect — just push the in-app route.
  if (!isMainSite()) {
    if (to.meta.requiresAuth && !auth.isAuthenticated) {
      if (isSingleRegion()) {
        return { path: '/login', query: { next: to.fullPath } };
      }
      const next = encodeURIComponent(window.location.origin + to.fullPath);
      window.location.href = `${MAIN_SITE_URL}/login?next=${next}`;
      return false;
    }
    // Module permissions: blocked module or a non-manager hitting the
    // Permission page → back to dashboard. (Backend enforces the same via
    // before_request/_require_manager, this just keeps the UX clean.)
    if (to.meta.adminOnly && !auth.canManageUsers) {
      return { name: 'dashboard' };
    }
    if (to.meta.module && !auth.hasModule(to.meta.module)) {
      return { name: 'dashboard' };
    }
    return;
  }

  // Main site: standard SPA login flow (only used if a public page somehow
  // requires auth — currently none do, but keep the guard for safety).
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } };
  }
});

export default router;
